import { Aktenzeichen } from './aktenzeichen.model';
import { RegisterName } from 'libs/register-shared/src/lib/register.model';
import { Verfahren } from 'libs/verfahren-shared/src/lib/verfahren.model';

/**
 * Unterstützt unter anderem bei der Register-spezifischen Extraktion von Aktenzeichenbestandteilen sowie dem Parsen
 * von Aktenzeichen aus Strings und Verfahren.
 *
 * Besitzt ein Pendant im Backend: AktenzeichenUtil.java
 */
export class AktenzeichenUtil {
	private static readonly FACTOR = 100000000;

	public static getBlattNummer(aktenzeichen: Aktenzeichen | string): string {
		if (typeof aktenzeichen === 'string') {
			aktenzeichen = this.parseString(aktenzeichen);
		}

		switch (aktenzeichen.register) {
			case RegisterName.AR: {
				return (
					this.getBlattNummerString(aktenzeichen).padStart(3, '0') + '/' + this.getYearString(aktenzeichen)
				);
			}
			case RegisterName.ETL:
				return this.getBlattNummerString(aktenzeichen) + 'E';
			default:
				return this.getBlattNummerString(aktenzeichen);
		}
	}

	public static getRegister(aktenzeichen: Aktenzeichen): string {
		if (aktenzeichen.register == RegisterName.ETL) {
			return 'LR';
		}
		return aktenzeichen.register.toString();
	}

	public static isValidAktenzeichen(aktenzeichen: string): boolean {
		if (!aktenzeichen) {
			return false;
		}
		const [amtsgerichtsNummer, register, blattNummer] = aktenzeichen.split(' ', 3);

		if (register == 'AR' && blattNummer?.includes('/')) {
			const [nr, jahr] = blattNummer.split('/', 2);
			return !isNaN(Number(nr)) && !isNaN(Number(jahr));
		}

		if (blattNummer?.endsWith('E')) {
			if (register === 'LR') {
				return !isNaN(Number(amtsgerichtsNummer)) && !isNaN(Number(blattNummer.slice(0, -1)));
			} else {
				return false;
			}
		}

		return !isNaN(Number(amtsgerichtsNummer)) && register in RegisterName && !isNaN(Number(blattNummer));
	}

	public static parse(aktenzeichen: string | Verfahren): Aktenzeichen {
		return typeof aktenzeichen === 'string' ? this.parseString(aktenzeichen) : this.parseVerfahren(aktenzeichen);
	}

	private static getYearString(aktenzeichen: Aktenzeichen): string {
		return aktenzeichen.blattNummer.toString().slice(0, 4);
	}

	private static getBlattNummerString(aktenzeichen: Aktenzeichen): string {
		return (aktenzeichen.blattNummer % this.FACTOR).toString();
	}

	private static parseString(aktenzeichen: string): Aktenzeichen {
		if (!AktenzeichenUtil.isValidAktenzeichen(aktenzeichen)) {
			throw new Error(
				"Invalid Argument: '" +
					aktenzeichen +
					"' - Aktenzeichen must contain amtsgerichtsnummer + register + blattnummer."
			);
		}

		const [amtsgerichtsNummer, register, blattNummer] = aktenzeichen.split(' ', 3);

		if (register == 'AR' && blattNummer.includes('/')) {
			const [nr, jahr] = blattNummer.split('/', 2);
			return {
				amtsgerichtsNummer: Number(amtsgerichtsNummer),
				register: RegisterName.AR,
				blattNummer: this.FACTOR * Number(jahr) + Number(nr),
			};
		}

		if (register === 'LR' && blattNummer.endsWith('E')) {
			return {
				amtsgerichtsNummer: Number(amtsgerichtsNummer),
				register: RegisterName.ETL,
				blattNummer: Number(blattNummer.slice(0, -1)),
			};
		}

		return {
			amtsgerichtsNummer: Number(amtsgerichtsNummer),
			register: RegisterName[register],
			blattNummer: Number(blattNummer),
		};
	}

	private static parseVerfahren(verfahren: Verfahren): Aktenzeichen {
		return {
			amtsgerichtsNummer: verfahren.amtsgerichtNummer,
			blattNummer: verfahren.blattNummer,
			register: verfahren.register,
		};
	}
}
